/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {error, info} from '@ion/core/src/logger/logger';

/**
 * Provides the logger to be invoked when loading an external theme.
 */
export const themeLoading = (url?: string) => {
  info(normalize('Loading External Theme', url));
};

/**
 * Provides the logger to be invoked when loading an external theme succeeded.
 */
export const themeLoaded = (url?: string) => {
  info(normalize('Successfully Loaded External Theme', url));
};

/**
 * Provides the logger to be invoked when loading an external theme failed.
 */
export const themeFailed = (url?: string, err?: Error) => {
  const message = 'An Error occurred while attempting to load the External Theme';
  if (err) {
    error(normalize(message, url), err.toString());
  } else {
    error(normalize(message, url));
  }
};

/**
 * Provides the logger to be invoked when an external theme has been loaded from cache.
 */
export const themeFromCache = (url?: string) => {
  info(normalize('Theme Successfully retrieved from Cache', url, 'for'));
};

/**
 * Provides the logger to be invoked when an external theme has been cached.
 */
export const themeCached = (url?: string) => {
  info(normalize('Successfully Cached External Theme', url));
};

/**
 * Provides the logger to be invoked when an external theme is not to be cached.
 */
export const themeCacheBypassed = (url?: string) => {
  info(normalize('By-Passed Persisting External Theme to Cache', url));
};

/**
 * Provides the logger to be invoked when an external theme is undefined.
 */
export const themeUnresolved = (url?: string) => {
  info(normalize('External Theme could not be resolved', url));
};

/**
 * Provides the logger to be invoked when an external theme has been initialized.
 */
export const themeInitialized = (url?: string) => {
  info(normalize('Successfully initialized External Theme', url));
};

/**
 * Provides the logger to be invoked when loading an external theme assets.
 */
export const assetsLoading = (backgroundImageURL?: string, logoImageUrl?: string) => {
  info(normalize('Loading Background Image', backgroundImageURL));
  info(normalize('Loading Logo Image', logoImageUrl));
};

/**
 * Provides the logger to be invoked when loading an external theme assets succeeded.
 */
export const assetsLoaded = () => {
  info(normalize('Successfully Loaded Background Image'));
  info(normalize('Successfully Loaded Logo Image'));
};

/**
 * Provides the logger to be invoked when loading an external theme assets failed.
 */
export const assetsFailed = (backgroundImageURL?: string, logoImageUrl?: string) => {
  error(normalize('An Error occurred while attempting to load Background Image', backgroundImageURL));
  error(normalize('An Error occurred while attempting to load Logo Image', logoImageUrl));
};

/**
 * Provides a convenience function for normalizing theme logging messages.
 */
const normalize = (message: string, url?: string, prefix = 'from') => (
  `[External Theme]: ${message}${url ? ` ${prefix} URL: ${url}` : '.'}`
);
